import React, { useEffect, useState } from "react";

import {
  Edit,
  Loader2,
  MapPin,
  Package,
  Save,
  Warehouse,
  X,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { WAREHOUSE_ADDRESS } from "~/constants/warehouse";
import { PICKUP_ADDRESS_ID } from "~/constants/warehouse";
import { toast } from "~/hooks/use-toast";
import { useClientAddresses } from "~/hooks/useClientAddresses";
import { supabase } from "~/integrations/supabase/client";
import AddressSelector from "~/pages/clients/components/client-detail/purchase-order/components/AddressSelector";
import { useDispatchGuide } from "~/pages/operations/hooks/useDispatchGuide";
import { SalesRequest } from "~/types/client";
import { isPickupAddress } from "~/utils/addressUtils";

interface AddressEditSectionProps {
  selectedOrder: SalesRequest;
  onOrderUpdate: () => void;
}

export const AddressEditSection: React.FC<AddressEditSectionProps> = ({
  selectedOrder,
  onOrderUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    selectedOrder.deliveryAddressId || null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [existingGuides, setExistingGuides] = useState<any[]>([]);

  const {
    addresses,
    isLoading: addressesLoading,
    fetchAddresses,
  } = useClientAddresses(selectedOrder.clientId);

  const { isCreatingDispatchGuide } = useDispatchGuide({
    refreshPageWithParams: true,
  });

  // Check if address editing is allowed
  const canEditAddress = !["in_transit", "delivered"].includes(
    selectedOrder.status
  );

  // Wrapper for fetchAddresses to match expected signature
  const handleFetchAddresses = async () => {
    await fetchAddresses();
  };

  // Fetch existing dispatch guides
  useEffect(() => {
    const fetchExistingGuides = async () => {
      try {
        const { data, error } = await supabase
          .from("sales_request_documents")
          .select("*")
          .eq("sales_request_id", selectedOrder.id)
          .eq("document_type", "guia_de_despacho");

        if (!error && data) {
          setExistingGuides(data);
        }
      } catch (error) {
        console.error("Error fetching existing guides:", error);
      }
    };

    fetchExistingGuides();
  }, [selectedOrder.id]);

  // Get current address display
  const getCurrentAddressDisplay = () => {
    if (isPickupAddress(selectedOrder.deliveryAddressId)) {
      return {
        type: "pickup",
        display: "Cliente retira en bodega",
        details: `${WAREHOUSE_ADDRESS.name} - ${WAREHOUSE_ADDRESS.street}`,
        fullDetails: `${WAREHOUSE_ADDRESS.comuna}, ${WAREHOUSE_ADDRESS.region}`,
      };
    }

    if (selectedOrder.deliveryAddressId && addresses.length > 0) {
      const address = addresses.find(
        (addr) => addr.id === selectedOrder.deliveryAddressId
      );
      if (address) {
        return {
          type: "delivery",
          display: `${address.street} ${address.number}`,
          details: address.complement || "",
          fullDetails: `${address.comuna && typeof address.comuna === "string" ? `${address.comuna}, ` : ""}${address.city || ""}`,
        };
      }
    }

    // Fallback to inline address data
    if (selectedOrder.deliveryStreet) {
      return {
        type: "delivery",
        display: `${selectedOrder.deliveryStreet} ${selectedOrder.deliveryNumber || ""}`,
        details: selectedOrder.deliveryComplement || "",
        fullDetails: `${selectedOrder.deliveryComuna && typeof selectedOrder.deliveryComuna === "string" ? `${selectedOrder.deliveryComuna}, ` : ""}${selectedOrder.deliveryCity || ""}`,
      };
    }

    return {
      type: "unknown",
      display: "Dirección no especificada",
      details: "",
      fullDetails: "",
    };
  };

  const currentAddress = getCurrentAddressDisplay();

  const handleSaveAddress = async () => {
    if (!selectedAddressId) {
      toast({
        title: "Error",
        description: "Debe seleccionar una dirección",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    try {
      let updateData;

      if (selectedAddressId === PICKUP_ADDRESS_ID) {
        // Handle pickup address - nullify address_id and use warehouse data in inline fields
        updateData = {
          delivery_address_id: null,
          delivery_street: WAREHOUSE_ADDRESS.street,
          delivery_number: WAREHOUSE_ADDRESS.number,
          delivery_complement: `${WAREHOUSE_ADDRESS.name} - Cliente retira en bodega`,
          delivery_city: WAREHOUSE_ADDRESS.city,
          delivery_state: WAREHOUSE_ADDRESS.region,
          delivery_comuna: WAREHOUSE_ADDRESS.comuna,
          updated_at: new Date().toISOString(),
        };
      } else {
        // Handle regular address - fetch the actual address data to sync denormalized fields
        const { data: addressData, error: addressError } = await supabase
          .from("addresses")
          .select("*")
          .eq("id", selectedAddressId)
          .single();

        if (addressError) {
          throw addressError;
        }

        updateData = {
          delivery_address_id: selectedAddressId,
          delivery_street: addressData.street,
          delivery_number: addressData.number,
          delivery_complement: addressData.complement,
          delivery_city: addressData.city,
          delivery_state: addressData.state,
          delivery_comuna: addressData.comuna,
          updated_at: new Date().toISOString(),
        };
      }

      // Update the sales request with new address and sync denormalized fields
      const { error } = await supabase
        .from("sales_request")
        .update(updateData)
        .eq("id", selectedOrder.id);

      if (error) {
        throw error;
      }

      toast({
        title: "Dirección actualizada",
        description: "La dirección de entrega ha sido actualizada exitosamente",
        variant: "default",
      });

      // Store the current sales request ID to reopen the dialog after refresh
      localStorage.setItem("reopenOrderDialog", selectedOrder.id);

      // Navigate to client page with sales request tab and specific sales request
      window.location.href = `/clients/${selectedOrder.clientId}?tab=sales-requests&sales_request=${selectedOrder.id}`;
    } catch (error) {
      console.error("Error updating address:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar la dirección",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setSelectedAddressId(selectedOrder.deliveryAddressId || null);
    setIsEditing(false);
  };

  if (!canEditAddress) {
    return (
      <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-5 py-3">
          <h3 className="flex items-center text-lg font-semibold text-gray-800">
            <MapPin className="mr-2 h-5 w-5 text-gray-600" />
            Dirección de Entrega
          </h3>
          <Badge variant="outline" className="text-gray-600">
            Solo lectura
          </Badge>
        </div>
        <div className="p-4">
          <div className="rounded-lg bg-gray-50 p-3">
            <p className="font-medium text-gray-900">
              {currentAddress.display}
            </p>
            {currentAddress.details && (
              <p className="text-sm text-gray-600">{currentAddress.details}</p>
            )}
            <p className="text-sm text-gray-500">
              {currentAddress.fullDetails}
            </p>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            La dirección no puede editarse después de que el pedido ha sido
            aceptado
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-5 py-3">
        <h3 className="flex items-center text-lg font-semibold text-gray-800">
          <MapPin className="mr-2 h-5 w-5 text-gray-600" />
          Dirección de Entrega
          {existingGuides.length > 0 && (
            <Badge variant="outline" className="ml-2">
              <Package className="mr-1 h-3 w-3" />
              Con guías
            </Badge>
          )}
        </h3>
        {!isEditing && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsEditing(true)}
          >
            <Edit className="mr-2 h-4 w-4" />
            Editar
          </Button>
        )}
      </div>

      <div className="p-4">
        {!isEditing ? (
          <div className="rounded-lg bg-gray-50 p-3">
            <p className="font-medium text-gray-900">
              {currentAddress.display}
            </p>
            {currentAddress.details && (
              <p className="text-sm text-gray-600">{currentAddress.details}</p>
            )}
            <p className="text-sm text-gray-500">
              {currentAddress.fullDetails}
            </p>
            {currentAddress.type === "pickup" && (
              <Badge variant="secondary" className="mt-2 gap-1">
                <Warehouse className="h-3 w-3" />
                Cliente retira
              </Badge>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <AddressSelector
              addresses={addresses}
              selectedAddressId={selectedAddressId}
              onSelect={setSelectedAddressId}
              isLoading={addressesLoading}
              clientId={selectedOrder.clientId}
              fetchAddresses={handleFetchAddresses}
            />

            {existingGuides.length > 0 && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <p className="text-sm font-medium text-yellow-800">
                  ⚠️ Cambio de dirección con guías existentes
                </p>
                <p className="mt-1 text-xs text-yellow-700">
                  Al cambiar la dirección se generará automáticamente una nueva
                  guía de despacho. Las guías anteriores se mantendrán como
                  referencia.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelEdit}
                disabled={isSaving || isCreatingDispatchGuide}
              >
                <X className="mr-2 h-4 w-4" />
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleSaveAddress}
                disabled={
                  isSaving || isCreatingDispatchGuide || !selectedAddressId
                }
              >
                {isSaving || isCreatingDispatchGuide ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isCreatingDispatchGuide
                      ? "Generando guía..."
                      : "Guardando..."}
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Guardar
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
